import PropTypes from 'prop-types';
import React, { Fragment, useEffect, useMemo, useState } from 'react';

import { apiRequest } from 'common-src/features/rest';
import { isFeatureEnabled } from 'common-src/models/FeatureFlag/selectors';
import Patient from 'common-src/models/Patient';
import { patientHasPrimaryCaregiver } from 'common-src/models/PatientCaregiver';
import {
  getSectionName,
  moduleTypes,
  sidebarModulesPriority,
} from 'common-src/models/PatientInteraction';
import { getAllowedDocumentationModules } from 'common-src/models/PatientInteraction/helpers';

import useCustomSelector from 'src/hooks/useCustomSelector';
import { excludedModulesIds } from 'src/pages/DocumentationIndex/constants';

import { getAllowedTemplates } from './helpers';
import { ModuleItem } from './items';
import styles from './SectionAside.module.scss';

const SectionAside = ({ onTemplateSelect, patient, templates }) => {
  const [selectedLink, setSelectedLink] = useState({});
  const [allowedTemplateTypes, setAllowedTemplateTypes] = useState([]);

  const allowedModules = useCustomSelector((state) => getAllowedDocumentationModules(state));
  const isGuideEnabled = useCustomSelector((state) => isFeatureEnabled(state, 'guide'));
  const hasPrimaryCaregiver = useCustomSelector((state) =>
    patientHasPrimaryCaregiver(state, patient?.id),
  );

  const allowedTemplates = useMemo(
    () => getAllowedTemplates(allowedTemplateTypes, allowedModules),
    [allowedTemplateTypes, allowedModules],
  );

  const excludedIds = useMemo(() => {
    const ids = excludedModulesIds;

    if (!patient.mfaApprovedAt) {
      if (!ids.includes(moduleTypes.MFARenewalRequest.id)) {
        ids.push(moduleTypes.MFARenewalRequest.id);
      }
    } else {
      const mfaRenewalRequestIndex = ids.indexOf(moduleTypes.MFARenewalRequest.id);
      if (mfaRenewalRequestIndex > -1) {
        ids.splice(mfaRenewalRequestIndex, 1);
      }
    }

    // Hide ZBI assessment if patient doesn't have primary caregiver
    if (!hasPrimaryCaregiver) {
      if (!ids.includes(moduleTypes.MindyZBI22Assessment.id)) {
        ids.push(moduleTypes.MindyZBI22Assessment.id);
      }
    } else {
      const zbiAssessmentIndex = ids.indexOf(moduleTypes.MindyZBI22Assessment.id);
      if (zbiAssessmentIndex > -1) {
        ids.splice(zbiAssessmentIndex, 1);
      }
    }

    return ids;
  }, [patient.mfaApprovedAt, hasPrimaryCaregiver]);

  useEffect(() => {
    apiRequest({
      endpoint: 'templates/allowed',
    })
      .then((res) => res.json())
      .then(setAllowedTemplateTypes);
  }, []);

  const renderModules = () =>
    Object.entries(allowedTemplates).map(([section, moduleIds]) => {
      if (section === 'guide' && (!isGuideEnabled || !patient?.isGuide)) return;
      if (moduleIds.size === 0) return;

      return (
        <Fragment key={section}>
          <p
            className={[styles.header, 'font-w-600', 'p-l-20', 'font-s-18'].join(' ')}
          >{`${getSectionName(section)} Documents`}</p>
          {Array.from(moduleIds)
            .filter((moduleId) => !excludedIds.includes(moduleId))
            .sort((a, b) => {
              const aPriority = sidebarModulesPriority[a];
              const bPriority = sidebarModulesPriority[b];

              if (aPriority !== bPriority) return bPriority - aPriority;

              const aTitle = Object.values(moduleTypes).find(
                (moduleType) => moduleType.id === a,
              ).title;
              const bTitle = Object.values(moduleTypes).find(
                (moduleType) => moduleType.id === b,
              ).title;

              return aTitle.localeCompare(bTitle);
            })
            .map((moduleId) => (
              <ModuleItem
                key={moduleId}
                moduleId={moduleId}
                setSelectedLink={setSelectedLink}
                selectedLink={selectedLink}
                onChangeHandler={() => {
                  onTemplateSelect(moduleId);
                  if (selectedLink[moduleId]) {
                    setSelectedLink({});
                  }
                }}
                template={templates.find((template) => template.templateType === moduleId)}
              />
            ))}
        </Fragment>
      );
    });

  return (
    <aside className={[styles.aside, 'p-t-30', 'primary-border-r'].join(' ')}>
      <p className={[styles.description, 'font-s-14'].join(' ')}>
        Select relevant form templates from this menu, then record your responses.
      </p>
      {renderModules()}
    </aside>
  );
};

SectionAside.propTypes = {
  onTemplateSelect: PropTypes.func,
  patient: PropTypes.exact(Patient.schema),
  templates: PropTypes.arrayOf(PropTypes.object),
};

export default SectionAside;
